import { useMemo } from 'react';
import { Link, Star, StarOutline } from '@mui/icons-material';
import { Divider } from '@mui/material';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { ROUTES } from '../../../constants';
import { favourizeCompany } from '@dh/api/users';
import { useBreakpoint, useNavigateTo, useTheme, useUser } from '@dh/hooks';
import {
    BodyLarge,
    Center,
    Column,
    DisplayLarge,
    HeadlineLarge,
    HeadlineSmall,
    Row,
    TitleLarge,
    TitleMedium,
    WrapContainer
} from '@dh/styles';
import { getBreakpointValue, Institution, isExpanded, Software } from '@dh/types';
import { isCompany, openInNewTab } from '@dh/utils';
import {
    ContactInfoInstitution,
    EmptyProfileIcon,
    FormattedOutput,
    ImageSlider,
    InstitutionIcon
} from '@dh/components';

import { Hint } from '../hint/Hint';
import { DigitalHubChip, FilledButton, OutlinedButton, TextButton } from '../buttons';
import { InstitutionOptionsMenu } from './institution-options/InstitutionOptionsMenu';
import {
    EmptyProfileContainer,
    EmptyProfileTextContainer,
    InstitutionCard,
    InstitutionCardContent,
    InstitutionControls,
    ProfileMainContainer,
    ProfileRightContainer,
    SoftwareCard,
    SoftwareCardContent,
    SoftwareDescription,
    SoftwareGrid,
    SoftwareIcon,
    SoftwareSection,
    TitlePicture,
    TitlePicturePlaceHolder
} from './styles';

export type Props = {
    institution: Institution;
    isAdmin: boolean;
    isProfile?: boolean;
    isFavorite?: boolean;
};

const InstitutionProfile = ({ institution, isAdmin, isProfile = false }: Props) => {
    const { documentId, name, logo, media, isPublic, slogan, info, used_software } = institution;
    const breakpoint = useBreakpoint();
    const { colors } = useTheme();
    const t = useTranslations('profile');
    const navigateTo = useNavigateTo();
    const { user, refreshUser } = useUser({ withFavourites: true });

    const isFavoriteCompany = useMemo(() => {
        if (user) return user.favouriteCompanies.some((c) => c.documentId === documentId);
        else return false;
    }, [user, documentId]);

    const isCompanyProfile = isCompany(institution);
    // TODO: empty if no matching criteria is defined
    const isEmptyCompanyProfile = isCompanyProfile && !info;

    const onStarClick = () => {
        if (user && isCompanyProfile)
            favourizeCompany(user.id.toString(), institution, !isFavoriteCompany).then(refreshUser);
    };

    const renderLogoPlaceholder = () => (
        <TitlePicturePlaceHolder>
            <InstitutionIcon type={institution.type} size={120} sx={{ color: colors.white }} />
        </TitlePicturePlaceHolder>
    );

    const institutionImages = media?.map((media) => (
        <div
            key={media.uniqueMediaId}
            style={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
            <Image
                priority
                alt={media.description ?? 'Hochgeladenes Bild des Unternehmens'}
                src={media.url}
                height={360}
                width={480}
                style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: 'auto',
                    objectFit: 'contain'
                }}
            />
        </div>
    ));

    const renderSoftwareCard = (software: Software) => (
        <SoftwareCard key={software.documentId}>
            <SoftwareCardContent>
                <Row gap={2}>
                    <SoftwareIcon />
                    <TitleMedium>{software.name}</TitleMedium>
                </Row>
                <SoftwareDescription>{software.description}</SoftwareDescription>
                <TextButton
                    withUnderline
                    icon={<Link sx={{ fontSize: 18 }} />}
                    label={`${software.name} Repository`}
                    onClick={() => openInNewTab(software.repository_link)}
                    sx={{ width: 'fit-content' }}
                />
            </SoftwareCardContent>
        </SoftwareCard>
    );

    const renderLeftContainer = () => (
        <Column width={getBreakpointValue(breakpoint, '100%', '100%', '65%')} gap={2} flex={1}>
            {institutionImages && institutionImages.length > 0 && (
                <ImageSlider images={institutionImages} />
            )}

            {info && (
                <>
                    <TitleLarge>{info.title}</TitleLarge>
                    <FormattedOutput>{info.text}</FormattedOutput>
                </>
            )}

            {used_software && used_software.length > 0 && (
                <SoftwareSection>
                    <TitleLarge color={colors.white}>{t('sections.open-source.title')}</TitleLarge>
                    <SoftwareGrid>{used_software.map(renderSoftwareCard)}</SoftwareGrid>
                </SoftwareSection>
            )}
        </Column>
    );

    const renderRightContainer = () => (
        <ProfileRightContainer breakpoint={breakpoint}>
            {logo ? (
                <TitlePicture
                    priority
                    src={logo.url}
                    alt={`Logo of ${name}`}
                    layout='responsive'
                    width={4} // only affects aspect ratio
                    height={3} // only affects aspect ratio
                />
            ) : (
                renderLogoPlaceholder()
            )}
            <ContactInfoInstitution institution={institution} />

            {isCompanyProfile && institution.topicsKnowledge && institution.topicsKnowledge.length > 0 && (
                <InstitutionCard>
                    <InstitutionCardContent>
                        <TitleLarge>{t('sections.topic-knowledge.title')}</TitleLarge>
                        <WrapContainer gap={2} justifyContent='flex-start'>
                            {institution.topicsKnowledge
                                ?.sort((tk1, tk2) => tk2.knowledge - tk1.knowledge)
                                .map((tk) => (
                                    <DigitalHubChip
                                        key={tk.topic.documentId}
                                        label={`${tk.topic.title}`}
                                        backgroundColor={colors.white}
                                    />
                                ))}
                        </WrapContainer>
                    </InstitutionCardContent>
                </InstitutionCard>
            )}
            {isCompanyProfile && (
                <InstitutionCard>
                    <InstitutionCardContent>
                        <TitleLarge>{t('sections.principles.title')}</TitleLarge>
                        <WrapContainer gap={2} justifyContent='flex-start'>
                            {institution.principles.map((principle, index) => (
                                <DigitalHubChip
                                    key={principle.documentId}
                                    label={`${index + 1}. ${principle.title}`}
                                    backgroundColor={colors.white}
                                />
                            ))}
                        </WrapContainer>
                    </InstitutionCardContent>
                </InstitutionCard>
            )}
        </ProfileRightContainer>
    );

    const renderEmptyCompanyProfile = () => (
        <EmptyProfileContainer p={6} gap={5}>
            <EmptyProfileIcon />
            <EmptyProfileTextContainer>
                <HeadlineLarge>{t('empty-profile.headline')}</HeadlineLarge>
                <BodyLarge color={colors.onSurfaceVariant}>
                    {t('empty-profile.description')}
                </BodyLarge>
            </EmptyProfileTextContainer>
            <Center gap={1}>
                <FilledButton
                    label={t('actions.edit-profile')}
                    onClick={() => navigateTo(ROUTES.EditInstitution)}
                />
                <OutlinedButton
                    label={t('actions.goto-faq')}
                    onClick={() => navigateTo(ROUTES.Faq)}
                />
            </Center>
        </EmptyProfileContainer>
    );

    const renderControls = () => (
        <InstitutionControls>
            {isCompanyProfile && !isPublic && <Hint text={t('profile-invisible')} />}
            <InstitutionOptionsMenu institution={institution} />
        </InstitutionControls>
    );

    const renderFavorizeButton = () =>
        user === undefined ? null : (
            <TextButton
                icon={isFavoriteCompany ? <Star /> : <StarOutline />}
                label={
                    isFavoriteCompany
                        ? t('actions.favourite-bookmarked')
                        : t('actions.favourite-bookmark')
                }
                onClick={onStarClick}
                sx={{ alignSelf: 'flex-end' }}
            />
        );

    return isEmptyCompanyProfile ? (
        renderEmptyCompanyProfile()
    ) : (
        <>
            {isAdmin && isProfile && renderControls()}
            <Column>
                <DisplayLarge color={colors.onPrimaryDark}>{name}</DisplayLarge>
                {slogan && (
                    <HeadlineSmall mt={1} color={colors.onPrimaryDark}>
                        {slogan}
                    </HeadlineSmall>
                )}
                {!isProfile && renderFavorizeButton()}
                <ProfileMainContainer breakpoint={breakpoint} mt={2}>
                    {renderLeftContainer()}
                    {isExpanded(breakpoint) && (
                        <Divider
                            orientation={'vertical'}
                            flexItem
                            sx={{ borderColor: colors.primary }}
                        />
                    )}
                    {renderRightContainer()}
                </ProfileMainContainer>
            </Column>
        </>
    );
};

export { InstitutionProfile };
